import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown, BookOpen } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { translations } from '../translations';
import { Logo } from './Logo';

export function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const t = translations.navigation;

  const languages = [
    { code: 'fr', label: 'Français' },
    { code: 'en', label: 'English' }
  ];

  const dropdowns = {
    monitoring: [
      { path: '/monitoring/network', label: { fr: 'Surveillance Réseau', en: 'Network Monitoring' } },
      { path: '/monitoring/endpoint', label: { fr: 'Surveillance des Endpoints', en: 'Endpoint Monitoring' } },
      { path: '/monitoring/cloud', label: { fr: 'Sécurité Cloud', en: 'Cloud Security' } },
      { path: '/monitoring/application', label: { fr: 'Sécurité des Applications', en: 'Application Security' } },
      { path: '/monitoring/logs', label: { fr: 'Gestion des Logs', en: 'Log Management' } },
    ],
    incidentResponse: [
      { path: '/incident-response', label: { fr: 'Vue d\'ensemble', en: 'Overview' } },
      { path: '/incident-response/forensic', label: { fr: 'Analyse Forensique', en: 'Forensic Analysis' } },
    ],
    threatHunting: [
      { path: '/threat-hunting', label: { fr: 'Vue d\'ensemble', en: 'Overview' } },
      { path: '/threat-hunting/active', label: { fr: 'Chasse Active', en: 'Active Hunting' } },
    ],
    siem: [
      { path: '/siem', label: { fr: 'Vue d\'ensemble', en: 'Overview' } },
      { path: '/siem/log-analysis', label: { fr: 'Analyse des Logs', en: 'Log Analysis' } },
    ],
  };

  const handleDropdownClick = (dropdown: string) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const handleMouseEnter = (dropdown: string) => {
    setOpenDropdown(dropdown);
  };

  const handleMouseLeave = () => {
    setOpenDropdown(null);
  };

  const handleLanguageSelect = (langCode: 'fr' | 'en') => {
    setLanguage(langCode);
    setIsLangDropdownOpen(false);
  };

  return (
    <nav className="relative flex justify-between items-center mb-16">
      <Link to="/" className="flex items-center space-x-3 group">
        <div className="w-10 h-10 transition-transform duration-300 group-hover:scale-110">
          <Logo />
        </div>
        <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
          secusoc.io
        </span>
      </Link>

      {/* Language Dropdown */}
      <div className="relative">
        <button
          onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
          onBlur={() => setTimeout(() => setIsLangDropdownOpen(false), 200)}
          className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors duration-300"
        >
          <Globe size={20} className="text-blue-400" />
          <span className="text-white font-medium">
            {languages.find(lang => lang.code === language)?.label}
          </span>
          <ChevronDown 
            size={16} 
            className={`text-white transform transition-transform duration-300 ${isLangDropdownOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isLangDropdownOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-lg py-2 z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code as 'fr' | 'en')}
                className={`w-full px-4 py-2 text-left hover:bg-gray-700 transition-colors duration-200
                  ${language === lang.code ? 'text-blue-400' : 'text-white'}`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mobile menu button */}
      <button 
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="md:hidden text-white"
      >
        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Desktop Navigation */}
      <div className="hidden md:flex space-x-8">
        {Object.entries(dropdowns).map(([key, items]) => (
          <div 
            key={key}
            className="relative"
            onMouseEnter={() => handleMouseEnter(key)}
            onMouseLeave={handleMouseLeave}
          >
            <button 
              onClick={() => handleDropdownClick(key)}
              className="flex items-center space-x-1 text-white hover:text-blue-400"
            >
              <span>{t[key][language]}</span>
              <ChevronDown size={16} className={`transform transition-transform ${openDropdown === key ? 'rotate-180' : ''}`} />
            </button>
            
            {openDropdown === key && (
              <div className="absolute left-0 mt-2 w-64 bg-gray-900 rounded-lg shadow-lg py-2 z-50">
                {items.map((item, index) => (
                  <Link
                    key={index}
                    to={item.path}
                    className="block px-4 py-2 text-white hover:bg-gray-800 hover:text-blue-400"
                    onClick={() => setOpenDropdown(null)}
                  >
                    {item.label[language]}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}

        <Link to="/blog" className="text-white hover:text-blue-400 flex items-center">
          <BookOpen className="w-4 h-4 mr-2" />
          Blog
        </Link>
        <Link 
          to="/get-started" 
          className="bg-blue-500 px-6 py-2 rounded-lg text-white hover:bg-blue-600 transition-all duration-300 transform hover:scale-105"
        >
          {t.contact[language]}
        </Link>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="absolute top-full left-0 right-0 bg-gray-900 p-4 md:hidden">
          <div className="flex flex-col space-y-4">
            {Object.entries(dropdowns).map(([key, items]) => (
              <div key={key}>
                <button 
                  onClick={() => handleDropdownClick(key)}
                  className="flex items-center justify-between w-full text-white hover:text-blue-400"
                >
                  <span>{t[key][language]}</span>
                  <ChevronDown size={16} className={`transform transition-transform ${openDropdown === key ? 'rotate-180' : ''}`} />
                </button>
                
                {openDropdown === key && (
                  <div className="pl-4 mt-2 space-y-2">
                    {items.map((item, index) => (
                      <Link
                        key={index}
                        to={item.path}
                        className="block text-white hover:text-blue-400"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setOpenDropdown(null);
                        }}
                      >
                        {item.label[language]}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <Link 
              to="/blog" 
              className="text-white hover:text-blue-400 flex items-center"
              onClick={() => setIsMenuOpen(false)}
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Blog
            </Link>
            <Link 
              to="/get-started"
              className="bg-blue-500 px-6 py-2 rounded-lg text-white hover:bg-blue-600 transition-all duration-300 text-center"
              onClick={() => setIsMenuOpen(false)}
            >
              {t.contact[language]}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}